import { createContext, useContext, useEffect } from "react"
import type { Route } from "types/not-yet-sorted"
import { isPreloadable } from "utils/lazyWithPreload"
import { runWhenIdle } from "utils/runWhenIdle"
import { routeForPath } from "./paths"

// Fetching page code before it's needed: every route component is
// lazyWithPreload (utils/routes/*), so its chunk can be requested on menu
// hover/focus and, for the few pages people open most, once the app is idle —
// opening the page then doesn't wait for its code.

/** Start loading the chunk of the page at `pathname` (no-op if unknown or already loaded). */
export const preloadRouteForPath = (pathname: string, routes: Route[]) => {
  const route = routeForPath(pathname, routes)
  if (route && isPreloadable(route.component)) route.component.preload().catch(() => undefined)
}

const RoutePreloadContext = createContext<(pathname: string) => void>(() => undefined)
export const RoutePreloadProvider = RoutePreloadContext.Provider
export const usePreloadRoute = () => useContext(RoutePreloadContext)

// The pages most sessions go on to open, per app.
export const IDLE_PRELOAD_PATHS: Record<"roads" | "rail", string[]> = {
  roads: ["/dashboard", "/detections-v2", "/route-view-v2"],
  rail: ["/", "/treatment-analysis", "/nrol"],
}

type NetworkInformationLike = { saveData?: boolean; effectiveType?: string }

const onConstrainedConnection = () => {
  const connection = (navigator as Navigator & { connection?: NetworkInformationLike }).connection
  return !!connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType ?? "")
}

/**
 * Once the browser is idle after the current page has started, load the
 * chunks of `paths` one after another (not on data-saver / 2G connections).
 */
export const useIdleRoutePreload = (paths: string[], routes: Route[], delayMs = 2000) => {
  const key = paths.join("|")
  useEffect(() => {
    if (onConstrainedConnection()) return undefined
    let cancelled = false
    const run = async () => {
      for (const path of paths) {
        if (cancelled) return
        const route = routeForPath(path, routes)
        if (route && isPreloadable(route.component)) await route.component.preload().catch(() => undefined)
      }
    }
    let cancelIdle = () => undefined as void
    const timer = window.setTimeout(() => {
      cancelIdle = runWhenIdle(() => void run())
    }, delayMs)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
      cancelIdle()
    }
    // routes change identity every render; the paths decide what to load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])
}
